import { Mail, Cloud, Send, ShieldCheck, Users, Archive } from 'lucide-react';
import { PillarPage } from './PillarPage';
import { MicrosoftBadge, GoogleBadge } from '../../components/PartnerBadges';

const services = [
  {
    icon: Mail,
    title: 'Business Email Setup',
    description:
      'Professional, domain-based email accounts (you@yourcompany.com) provisioned, secured, and ready for your team — on the platform that fits you best.',
    highlights: [
      'Domain-based email provisioning',
      'DNS, SPF, DKIM & DMARC configured correctly',
      'Mobile and desktop client setup',
      'User onboarding & training',
    ],
  },
  {
    icon: Cloud,
    title: 'Microsoft 365 & Google Workspace',
    description:
      'Full deployment of Microsoft 365 or Google Workspace — including licensing, identity, Teams / Meet, SharePoint / Drive, and ongoing administration.',
    highlights: [
      'License selection & cost optimization',
      'User identity & SSO setup',
      'Teams, SharePoint, OneDrive / Drive',
      'Admin policies & device management',
    ],
  },
  {
    icon: Send,
    title: 'Email Migration',
    description:
      'Move from Gmail, Outlook, IMAP, or legacy on-prem servers to modern cloud email — with zero lost messages and minimal downtime.',
    highlights: [
      'Full mailbox migration (mail, contacts, calendars)',
      'Phased cutover with no disruption',
      'Hybrid coexistence during transition',
      'Post-migration validation & support',
    ],
  },
  {
    icon: ShieldCheck,
    title: 'Email Security & Anti-Phishing',
    description:
      'Layered protection against phishing, spoofing, malware, and business email compromise — with active threat monitoring and policy enforcement.',
    highlights: [
      'Advanced threat protection (ATP)',
      'Anti-phishing & impersonation defense',
      'Safe Links, Safe Attachments',
      'DMARC enforcement & reporting',
    ],
  },
  {
    icon: Users,
    title: 'Shared Mailboxes & Collaboration',
    description:
      'Set up shared mailboxes, distribution groups, and team collaboration spaces so the right people see the right messages — without password-sharing.',
    highlights: [
      'Shared mailboxes (info@, sales@, support@)',
      'Distribution lists & dynamic groups',
      'Microsoft Teams / Google Spaces setup',
      'Permission & delegation policies',
    ],
  },
  {
    icon: Archive,
    title: 'Backup, Archiving & Compliance',
    description:
      'Protect your business communications with reliable backup, long-term archiving, and policies that meet regulatory and legal-hold requirements.',
    highlights: [
      'Automated email backup',
      'Long-term archiving & retention policies',
      'eDiscovery & legal hold',
      'Disaster recovery planning',
    ],
  },
];

const process = [
  { step: '01', label: 'Audit',    desc: 'We review your current setup, users, domains, and pain points.' },
  { step: '02', label: 'Plan',     desc: 'Migration strategy, license sizing, and rollout calendar agreed upfront.' },
  { step: '03', label: 'Migrate',  desc: 'Phased mailbox migration with full data preservation and zero downtime.' },
  { step: '04', label: 'Secure',   desc: 'Anti-phishing, DMARC, MFA, and backup policies enforced before launch.' },
  { step: '05', label: 'Support',  desc: 'Ongoing administration, user support, and quarterly health reviews.' },
];

const faqs = [
  {
    question: 'Should I choose Microsoft 365 or Google Workspace?',
    answer: 'Microsoft 365 is the better fit for organizations using Windows, Office desktop apps, Teams, SharePoint, and enterprise identity (Entra ID, Intune). Google Workspace suits teams that prefer browser-based collaboration with Gmail, Docs, and Drive. We help you compare cost, security, and feature fit before deciding.',
  },
  {
    question: 'How do you help us choose the right Microsoft 365 or Google Workspace plan?',
    answer: 'We assess your team size, security requirements, compliance needs, and the features you will actually use — then recommend the most cost-effective license tier. We help you avoid overpaying for features you do not need and make sure you are not underprotected on security.',
  },
  {
    question: 'How long does an email migration take?',
    answer: 'Most migrations complete in 1 to 4 weeks depending on user count, mailbox size, and source platform. We do a phased rollout — pilot users first, then department-by-department — to catch issues early and avoid mass-downtime risk.',
  },
  {
    question: 'Can you migrate our email without losing any data?',
    answer: 'Yes. We perform phased mailbox migrations with full data preservation — emails, calendars, contacts, shared drives, and historical archives. We use zero-downtime cutover strategies so users never miss a message during the move.',
  },
  {
    question: 'Can you migrate from cPanel, Zimbra, or another self-hosted email server?',
    answer: 'Yes. We migrate from cPanel, Zimbra, Exchange Server, Hostgator, Bluehost, IMAP-based hosts, and other legacy or self-hosted systems into Microsoft 365 or Google Workspace — preserving folder structures, sent items, calendar events, and shared mailboxes.',
  },
  {
    question: 'Do you set up SPF, DKIM, and DMARC?',
    answer: 'Always. Proper SPF, DKIM, and DMARC configuration is part of every email setup we deliver. This prevents spoofing, improves deliverability, and is increasingly required by Gmail, Yahoo, and other major providers.',
  },
  {
    question: 'Why are our emails landing in spam, and can you fix it?',
    answer: 'Yes. Most spam-folder issues come from missing or misconfigured SPF/DKIM/DMARC, a poor sender reputation, or shared IPs flagged by spam filters. We audit your DNS, authentication, sending volume, and content patterns, then fix the underlying issues — typically within a few days.',
  },
  {
    question: 'Can you handle identity and device management?',
    answer: 'Yes. We deploy and manage identity and access through Microsoft Entra ID (formerly Azure AD), Google Cloud Identity, MFA enforcement, conditional access, and device management via Intune or Google MDM.',
  },
  {
    question: 'Do you set up email backups?',
    answer: 'Yes. Microsoft and Google retain email for limited periods and treat user-deleted data as the user is responsibility. We deploy third-party backup solutions (Veeam, Datto, AvePoint) for full point-in-time restore of email, OneDrive/Drive, SharePoint/Shared Drives, and Teams data.',
  },
  {
    question: 'Can you train our team on the new platform?',
    answer: 'Yes. Every migration includes role-based training sessions — admins get deep-dives on the admin console, end-users get focused sessions on the apps they actually use, and we provide written quick-start guides tailored to your environment.',
  },
  {
    question: 'Do you offer ongoing email and workplace support?',
    answer: 'Yes. We provide ongoing administration, user support, license optimization, security policy enforcement, and quarterly health reviews to keep your environment secure and cost-efficient.',
  },
];

export const EmailWorkplacePage = () => (
  <PillarPage
    meta={{
      title: 'Email & Workplace Solutions',
      label: 'Email & Workplace',
      headline: 'Communication That Just Works',
      italicWord: 'Just Works',
      description:
        'Business-grade email, collaboration, and identity — set up, secured, and migrated by certified Microsoft and Google specialists. Zero lost messages, zero downtime.',
      servicesHeading: 'Everything Your Workplace Needs',
    }}
    badgeRow={<><MicrosoftBadge /><GoogleBadge /></>}
    theme={{
      primary: '#1E3A8A',
      accent:  '#00A4EF',
      bg:      '#EFF6FF',
      soft:    '#F0F9FF',
    }}
    services={services}
    process={process}
    cta={{
      headline: 'Ready to upgrade your business email?',
      italic:   "Let's set it up right.",
      buttonLabel: 'Plan Your Migration',
    }}
    seo={{
      title: 'Microsoft 365 & Google Workspace Email Setup',
      description: 'Business email, Microsoft 365, and Google Workspace deployment, migration, and support. SPF, DKIM, DMARC, MFA, and identity management handled by certified specialists.',
      path: '/services/email-workplace',
      serviceName: 'Email & Workplace Solutions',
    }}
    faqs={faqs}
  />
);
